import 'react-notifications/lib/notifications.css'
import React, { Component } from 'react'
import { withTranslation } from 'react-i18next'
import { NotificationContainer, NotificationManager } from 'react-notifications'
import { FaCheck, FaPencilAlt, FaTimes, FaPlus, FaCoins, FaFilter, FaGem, FaGift } from "react-icons/fa"
import AdminLayout from "../../../components/AdminLayout"

import CardService from "../../../services/card_services"

const CURRENCY_MAP = {
    0: { label: 'Coins', icon: FaCoins, color: 'text-yellow-600 bg-yellow-50' },
    1: { label: 'Gems', icon: FaGem, color: 'text-cyan-600 bg-cyan-50' },
    2: { label: 'IRL Reward', icon: FaGift, color: 'text-pink-600 bg-pink-50' },
}

class AdminPacks extends Component {
    constructor(props) {
        super(props)
        this.state = {
            packs: [],
            currency: CURRENCY_MAP,
            active: true,
            loading: true,
        }
    }

    componentDidMount = async () => {
        await this.loadPacks()
    }

    loadPacks = async () => {
        try {
            this.setState({ loading: true })
            const data = await CardService.getPacks(this.state.active)
            const currency = data.currency || CURRENCY_MAP
            this.setState({ packs: data.packs || [], currency, loading: false })
        } catch (e) {
            this.setState({ loading: false })
            NotificationManager.error(e.response && e.response.data ? e.response.data.message : e.message)
        }
    }

    switchFilter = () => {
        this.setState({ active: !this.state.active }, () => this.loadPacks())
    }

    activate = async (pack_uuid) => {
        try {
            const data = await CardService.activatePack(pack_uuid)
            NotificationManager.success(data.message)
            this.loadPacks()
        } catch (e) {
            NotificationManager.error(e.response && e.response.data ? e.response.data.message : e.message)
        }
    }

    deactivate = async (pack_uuid) => {
        try {
            const data = await CardService.deactivatePack(pack_uuid)
            NotificationManager.success(data.message)
            this.loadPacks()
        } catch (e) {
            NotificationManager.error(e.response && e.response.data ? e.response.data.message : e.message)
        }
    }

    setDefault = async (pack_uuid) => {
        if (!window.confirm('Set this pack as the default? This will replace the current default pack.')) return
        try {
            const data = await CardService.setPackDefault(pack_uuid)
            NotificationManager.success(data.message)
            this.loadPacks()
        } catch (e) {
            NotificationManager.error(e.response && e.response.data ? e.response.data.message : e.message)
        }
    }

    formatDate = (d) => {
        if (!d) return '-'
        try {
            return new Date(d).toLocaleDateString()
        } catch (_) {
            return d
        }
    }

    renderCurrencyBadge = (value) => {
        const def = CURRENCY_MAP[value] || { label: this.state.currency[value] || 'Unknown', icon: FaCoins, color: 'text-gray-600 bg-gray-100' }
        const Icon = def.icon
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${def.color}`}>
                <Icon /> {def.label}
            </span>
        )
    }

    render() {
        return (
            <>
                <AdminLayout
                    title="Packs / Boosters Management"
                    breadcrumbs={[{ label: 'Packs / Boosters' }]}
                >
                    <div className="space-y-6">
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={() => window.location.href = '/admin/packs/create'}
                                className="px-4 py-2 bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white rounded-lg hover:from-fuchsia-600 hover:to-purple-700 transition-all font-medium shadow-md hover:shadow-lg flex items-center gap-2"
                            >
                                <FaPlus />
                                Create Pack
                            </button>
                        </div>

                        {/* Filters Section */}
                        <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <FaFilter className="text-gray-600" />
                                Filters
                            </h3>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={this.switchFilter}
                                    className={`px-4 py-2 rounded-lg transition-all font-medium text-sm shadow-md ${
                                        this.state.active
                                            ? 'bg-green-500 text-white'
                                            : 'bg-gray-500 text-white'
                                    }`}
                                >
                                    {this.state.active ? 'Showing Active' : 'Showing Deactivated'}
                                </button>
                                <span className="text-sm text-gray-600">Click to toggle</span>
                            </div>
                        </div>

                        {/* Packs Grid */}
                        {this.state.loading ? (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                                Loading packs...
                            </div>
                        ) : this.state.packs.length === 0 ? (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                                No packs to display.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {[...this.state.packs]
                                    .sort((a, b) => {
                                        if (a.default_pack) return -1
                                        if (b.default_pack) return 1
                                        return 0
                                    })
                                    .map(pack => {
                                    const isDefault = !!pack.default_pack
                                    return (
                                    <div
                                        key={pack.pack_uuid}
                                        className={`relative rounded-xl overflow-hidden hover:shadow-xl transition-shadow flex flex-col ${
                                            isDefault
                                                ? 'p-[3px] bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-500 shadow-xl shadow-amber-300/60'
                                                : 'bg-white shadow-md border border-gray-200'
                                        }`}
                                    >
                                        {isDefault && (
                                            <>
                                                {/* Corner ribbon */}
                                                <div className="absolute -right-12 top-5 z-20 rotate-45 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-extrabold tracking-widest px-12 py-1 shadow-md uppercase">
                                                    Default
                                                </div>
                                            </>
                                        )}
                                        <div className={`flex-1 flex flex-col rounded-[10px] overflow-hidden ${isDefault ? 'bg-white' : ''}`}>
                                        {/* Cover */}
                                        <div className="relative">
                                            <div
                                                className="w-full h-48 bg-cover bg-center bg-gray-100"
                                                style={pack.cover ? { backgroundImage: `url('${pack.cover}')` } : {}}
                                            />
                                            {isDefault && (
                                                <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-extrabold bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg ring-2 ring-white">
                                                    <FaCoins className="animate-pulse" /> DEFAULT PACK
                                                </span>
                                            )}
                                            <span className={`absolute top-2 right-2 inline-block px-2 py-1 rounded-md text-xs font-medium shadow ${pack.sale_status ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>
                                                {pack.sale_status ? 'On sale' : 'Off sale'}
                                            </span>
                                        </div>

                                        {/* Body */}
                                        <div className={`p-4 flex-1 flex flex-col ${isDefault ? 'bg-gradient-to-b from-amber-50 to-white' : ''}`}>
                                            <h3 className={`text-lg font-bold truncate flex items-center gap-2 ${isDefault ? 'text-amber-700' : 'text-gray-900'}`} title={pack.pack_name}>
                                                {isDefault && <FaCoins className="text-amber-500 flex-shrink-0" />}
                                                {pack.pack_name}
                                            </h3>

                                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                                {this.renderCurrencyBadge(pack.currency)}
                                                <span className="inline-block px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                                                    {pack.price} ({this.state.currency[pack.currency] || ''})
                                                </span>
                                                <span className="inline-block px-2 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700">
                                                    {pack.nb_cards} cards
                                                </span>
                                            </div>

                                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                                <div>
                                                    <p className="font-semibold text-gray-500 uppercase">Start</p>
                                                    <p>{this.formatDate(pack.start_date)}</p>
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-gray-500 uppercase">End</p>
                                                    <p>{this.formatDate(pack.end_date)}</p>
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-gray-500 uppercase">Distributed</p>
                                                    <p>{pack.nb_distributed || 0}</p>
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-gray-500 uppercase">Collections</p>
                                                    <p>{pack.collections ? pack.collections.length : 0}</p>
                                                </div>
                                                {pack.rcat_product_id && (
                                                    <div className="col-span-2">
                                                        <p className="font-semibold text-gray-500 uppercase">RevenueCat Product</p>
                                                        <p className="truncate" title={pack.rcat_product_id}>{pack.rcat_product_id}</p>
                                                    </div>
                                                )}
                                            </div>

                                            {pack.description && (pack.description.en || pack.description.fr) && (
                                                <p className="mt-3 text-xs text-gray-600 line-clamp-2">
                                                    {pack.description.en || pack.description.fr}
                                                </p>
                                            )}

                                            {/* Actions */}
                                            <div className="mt-auto pt-4 border-t border-gray-100 flex flex-wrap gap-2">
                                                <button
                                                    onClick={() => window.location.href = `/admin/packs/update/${pack.pack_uuid}`}
                                                    className="flex-1 min-w-[80px] px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium flex items-center justify-center gap-1"
                                                >
                                                    <FaPencilAlt /> Edit
                                                </button>
                                                {!isDefault && (
                                                    <button
                                                        onClick={() => this.setDefault(pack.pack_uuid)}
                                                        className="flex-1 min-w-[80px] px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium flex items-center justify-center gap-1"
                                                        title="Set as default"
                                                    >
                                                        <FaCoins /> Default
                                                    </button>
                                                )}
                                                {this.state.active ? (
                                                    <button
                                                        onClick={() => this.deactivate(pack.pack_uuid)}
                                                        className="flex-1 min-w-[80px] px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium flex items-center justify-center gap-1"
                                                    >
                                                        <FaTimes /> Deactivate
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => this.activate(pack.pack_uuid)}
                                                        className="flex-1 min-w-[80px] px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium flex items-center justify-center gap-1"
                                                    >
                                                        <FaCheck /> Activate
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        </div>
                                    </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </AdminLayout>
                <NotificationContainer />
            </>
        )
    }
}

export default withTranslation()(AdminPacks)
